/**
 * Hook: POST /api/t/{tenantSlug}/interview-analysis — Batch 30j-3, widened
 * for `type:"post"` + transcript pass-through in Batch 30j-5.
 *
 * URL note (corrected in Batch 30j-4): see `useInterviewAnalysis` docstring.
 * The bare ``/api/pipeline/...`` path that originally shipped did not match
 * the tenant-router mount point at ``/api/t/{tenant_slug}``; aligned in 30j-4.
 *
 * Mutation to start an interview analysis (pre OR post). On success,
 * optimistically writes a synthesized `queued` row to the React Query cache
 * so the UI flips to `generating` phase IMMEDIATELY (no sub-second blank
 * flicker between mutation success and the first poll's response). Then
 * invalidates the polling query key so the real row arrives within ~one tick.
 *
 * Design references:
 *   - `frontend/docs/30j_3_design.md` §3.4 + §3.5 — optimistic write shape
 *   - `frontend/docs/30j_3_architect.md` §4.4 — mutation contract
 *   - `frontend/docs/30j_3_security.md` §6 (HARD-5) — optimistic write
 *     happens INSIDE `onSuccess`, NOT `onSettled`, so 409 / 5xx paths
 *     do NOT pollute the cache with synthetic rows that survive a failed start.
 *   - `frontend/docs/30j_5_architect.md` §2.5 — widening for post-variant.
 *   - `frontend/docs/30j_5_security.md` §4 HARD-1 — transcript ships on the
 *     wire on POST, but the consumer NEVER renders `data.transcript` to DOM.
 *
 * Hard rules:
 *   - For `type: "pre"`, `transcript` MUST be `null` (server validator).
 *   - For `type: "post"`, `transcript` MUST be a string of stripped length
 *     ≥ 50 (`backend/app/schemas/interview_analysis.py:35` — Architect AR-1
 *     ratification — backend canon supersedes HoD's 100-char hint).
 *   - `jd_id` is REQUIRED for both variants.
 *   - 409 → toast + invalidate. The mutation's `isError` flag is set,
 *     but the consumer checks the polling phase, not the mutation phase,
 *     so the visible UI stays `generating`.
 *   - 402 → toast surfaces "Out of credits".
 *   - 409 prerequisites_missing → toast surfaces "Run AI Evaluation first".
 *   - 409 already_generated → INFO toast ("already up to date — nothing to
 *     re-run"); the re-run kebab hits this for any candidate that already has
 *     a completed eval, so it MUST surface feedback rather than close silently.
 */
import {
  useMutation,
  useQueryClient,
  type UseMutationResult,
} from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { ApiError } from "@/lib/apiError";
import { apiClient } from "@/lib/apiClient";
import { useToastStore } from "@/stores/useToastStore";
import type { InterviewAnalysisRow } from "./useInterviewAnalysis";

interface StartArgs {
  candidate_id: string;
  type: "pre" | "post";
  jd_id: string;
  /** REQUIRED when type='post'; ignored / null when type='pre'. */
  transcript?: string;
}

interface StartResponse {
  id: string;
  status: "queued" | "running";
  type: "pre" | "post";
}

export function useStartInterviewAnalysis(): UseMutationResult<
  StartResponse,
  ApiError,
  StartArgs
> {
  const queryClient = useQueryClient();
  const { tenantSlug } = useParams<{ tenantSlug: string }>();
  const addToast = useToastStore((s) => s.addToast);

  return useMutation<StartResponse, ApiError, StartArgs>({
    mutationFn: async (args) => {
      const r = await apiClient.post<StartResponse>(
        `/api/t/${tenantSlug}/interview-analysis`,
        {
          candidate_id: args.candidate_id,
          type: args.type,
          jd_id: args.jd_id,
          transcript:
            args.type === "post" ? (args.transcript ?? null) : null,
        },
      );
      return r.data;
    },
    onSuccess: (response, vars) => {
      const queryKey = [
        "interview-analysis",
        "by-resource",
        tenantSlug,
        vars.candidate_id,
        vars.type,
        vars.jd_id,
      ];

      // Optimistic write — synthesize a queued row so the UI flips to
      // `generating` phase IMMEDIATELY. The next poll (triggered by the
      // invalidate below) replaces the synthesized row with the real one
      // inside ~500ms.
      const optimisticRow: InterviewAnalysisRow = {
        id: response.id,
        type: vars.type,
        candidate_id: vars.candidate_id,
        jd_id: vars.jd_id,
        status: response.status,
        analysis: null,
        model_used: null,
        transcript:
          vars.type === "post" ? (vars.transcript ?? null) : null,
        error: null,
        // started_by, created_at, completed_at filled by the next refetch.
        started_by: "",
        created_at: new Date().toISOString(),
        completed_at: null,
      };
      queryClient.setQueryData(queryKey, optimisticRow);
      void queryClient.invalidateQueries({ queryKey });
    },
    onError: (err, vars) => {
      // 409 `analysis_in_progress`: a row already exists for this
      // candidate. Toast a friendly message AND invalidate the polling
      // query so it picks up the existing in-flight row. Phase stays
      // `generating` (because the polling query has the real row); no
      // synthetic row is written.
      if (err instanceof ApiError && err.error === "analysis_in_progress") {
        addToast(
          vars.type === "post"
            ? "An analysis is already running for this candidate."
            : "An evaluation is already running for this candidate.",
          "warning",
        );
        void queryClient.invalidateQueries({
          queryKey: [
            "interview-analysis",
            "by-resource",
            tenantSlug,
            vars.candidate_id,
            vars.type,
            vars.jd_id,
          ],
        });
        return;
      }
      // 409 `prerequisites_missing`: defense-in-depth toast for post-tab
      // race (the frontend's strict gate per AR-8 should normally prevent
      // reaching this case).
      if (err instanceof ApiError && err.error === "prerequisites_missing") {
        addToast(
          "Run AI Evaluation first to unlock interview analysis.",
          "warning",
        );
        return;
      }
      // 409 `already_generated`: the candidate already has a completed
      // evaluation of this type (the common case when re-running from the
      // kebab on a demo candidate). This is NOT an error the user caused —
      // surface an INFO toast so the confirm modal doesn't just close in
      // silence. No cache write; the existing completed row stays visible.
      if (err instanceof ApiError && err.error === "already_generated") {
        addToast(
          "This evaluation is already up to date — nothing to re-run.",
          "info",
        );
        return;
      }
      // 402 InsufficientCredits — surface friendly toast pointing to billing.
      if (err instanceof ApiError && err.status === 402) {
        addToast("Out of credits — top up to continue.", "warning");
        return;
      }
      // Other errors bubble to the consumer's error UI (and any
      // generic toast surface). HARD-7 — never surface raw `err.error`
      // strings; the consumer translates.
    },
  });
}
